import { AnyAction } from 'redux';
import { v1 as uuidv1 } from 'uuid';
import {
  DoRequestResponse,
  GlobalActions,
  ShopTarifierungRequestEvents,
  StreckenTarifierungRequestEvents,
} from '@ps20-frontend/shop-integration-redux';
import { GegenstandCategory, GegenstandCategoryModel, TaasResponseModel } from '@ps20/bff-models';
import { convertDateToDayJs, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { App } from '@ps20-frontend/frontend-models';
import { GegeActions, GegenstaendeVorbelegtAction } from '../gegeActions';
import { IAppState } from '../appState';

export interface Gegenstandsschutz {
  categories?: GegenstandsschutzCategory[];
  items: GegenstandsschutzItem[];
  vorvertragHatFahrrad?: boolean;
}

export interface GegenstandsschutzItem {
  id: string;
  category: GegenstandsschutzCategory;
  purchaseDate: Date;
  insuranceValue: number;
  bruttoPrice?: number;
  yearlyNettoPrice?: number;
  model?: string;
  information?: string;
  anzahlVorschaeden?: number;
}

export const emptyGegenstand = (): GegenstandsschutzItem => {
  return { id: uuidv1(), category: null, purchaseDate: null, insuranceValue: null };
};

export interface GegenstandsschutzCategory {
  id: string;
  name: string;
  minPurchaseDate?: Date;
}

export const gegenstandsschutzReducer = (
  sectionState: Gegenstandsschutz = null,
  action: AnyAction,
  state: IAppState,
): Gegenstandsschutz => {
  switch (action.type) {
    case GegeActions.GEGENSTAENDE_ADDED: {
      const isPurchasePriceDecimalString = (gegenstand: GegenstandsschutzItem): boolean => {
        return (
          typeof gegenstand.insuranceValue === 'string' && /\d+/.test(gegenstand.insuranceValue)
        );
      };
      const items = (action.payload as GegenstandsschutzItem[]).map(gegenstand => ({
        ...gegenstand,
        insuranceValue: isPurchasePriceDecimalString(gegenstand)
          ? Number.parseInt(gegenstand.insuranceValue + '', 10)
          : gegenstand.insuranceValue,
      }));
      return {
        ...sectionState,
        items,
      };
    }
    case GegeActions.GEGENSTAENDE_VORBELEGT: {
      const actionPayload = (action as GegenstaendeVorbelegtAction).payload;
      if (actionPayload.angebotsId === state.angebotsId) {
        const items = actionPayload.gegenstaende.map(gegenstand => ({
          ...gegenstand,
          insuranceValue: Number.parseInt(gegenstand.insuranceValue + '', 10),
        }));
        return {
          ...sectionState,
          items,
          vorvertragHatFahrrad: items.some(
            gegenstand => gegenstand.category?.id === GegenstandCategory.FAHRRAD,
          ),
        };
      }
      return sectionState;
    }
    case GegeActions.GEGENSTAND_REMOVED: {
      return {
        ...sectionState,
        items: sectionState.items.filter(gegenstand => {
          return gegenstand.id !== action.payload.id;
        }),
      };
    }
    case GlobalActions.GEGENSTAENDE_CATEGORIES_RECEIVED: {
      const categories: GegenstandsschutzCategory[] = (
        action.payload as GegenstandCategoryModel[]
      ).map(gegenstandCategoryModel => {
        return {
          id: gegenstandCategoryModel.id,
          name: gegenstandCategoryModel.categoryName,
          minPurchaseDate: gegenstandCategoryModel.minDate
            ? convertDateToDayJs(gegenstandCategoryModel.minDate, DATEFORMAT_SHORT_ISO).toDate()
            : null,
        };
      });
      return {
        ...sectionState,
        categories,
      };
    }
    case ShopTarifierungRequestEvents.PREISE_SHOP_REQUESTED_SUCCESSFUL_GEGE:
    case StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_GEGE:
    case StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_GEGE: {
      const response = action as DoRequestResponse<TaasResponseModel>;
      if (
        response.doRequestAction?.payload?.appId === App.GEGE &&
        response.doRequestAction?.payload?.angebotsId === state.angebotsId
      ) {
        sectionState.items.forEach(item => {
          let gegenstandResponseModel = response.payload.gegenstaende.find(
            gegenstand => gegenstand.id === item.id,
          );
          if (state.transientState.featureToggles['PS20_ERSATZGESCHAEFT_GEGE_TARIFIERUNG_MOCK']) {
            gegenstandResponseModel = response.payload.gegenstaende[0];
          }
          const gegenstandPricing = gegenstandResponseModel.gegenstandPricings.find(
            pricing =>
              pricing.contractLengthInMonths / 12 === state.vertragsoptionen.vertragslaufzeit &&
              pricing.selfParticipation === state.vertragsoptionen.selbstbeteiligung &&
              pricing.paymentMethod === state.vertragsoptionen.zahlungsweise &&
              pricing.salesProductName.toUpperCase() ===
                state.produkt.produktlinien
                  .find(produktLinie => produktLinie.selected)
                  .produktName.toUpperCase(),
          );
          item.bruttoPrice = gegenstandPricing.bruttoPriceForPaymentMethod;
          item.yearlyNettoPrice = gegenstandPricing.yearlyNettoPrice;
        });
        return {
          ...sectionState,
          items: [...sectionState.items],
        };
      }
      return sectionState;
    }
    default:
      return sectionState;
  }
};
